import React, { useState, useEffect } from 'react';
import { ArrowLeft, Loader2, AlertCircle, RefreshCw, Clock, Save, ShieldAlert } from 'lucide-react';
import { useLazyGetAllServiceQuery } from '../../../redux/actions/Admin/ServiceAdmin';
import { 
  useLazyGetWorkingDayByServiceIdQuery, 
  useSaveWorkingDayMutation 
} from '../../../redux/actions/Admin/ServiceAdmin';
import { IserviceResponse } from '../../../types/Response/AdminResponse';
import { IServiceWorkingDaysRequest, IWorkingDay } from '../../../types/Request/AdminRequest';

const DAYS_OF_WEEK_NAMES: { [key: number]: string } = {
  0: "Sunday",
  1: "Monday",
  2: "Tuesday",
  3: "Wednesday",
  4: "Thursday",
  5: "Friday",
  6: "Saturday"
};

const ORDERED_DAYS_ARRAY = [0, 1, 2, 3, 4, 5, 6];

export default function ManageWeekdays() {
  const [triggerGetAllServices, { data: services = [], isLoading: servicesLoading, isError: servicesError }] = useLazyGetAllServiceQuery();
  const [triggerGetSchedule, { isLoading: scheduleLoading, isError: scheduleError }] = useLazyGetWorkingDayByServiceIdQuery();
  const [saveWorkingDay, { isLoading: isSaving }] = useSaveWorkingDayMutation();

  const [view, setView] = useState<'services-select' | 'schedule-editor'>('services-select');
  const [selectedService, setSelectedService] = useState<IserviceResponse | null>(null);
  const [workingDays, setWorkingDays] = useState<IWorkingDay[]>([]);

  // Initial services fetch
  useEffect(() => {
    triggerGetAllServices();
  }, [triggerGetAllServices]);

  const handleSelectService = async (service: IserviceResponse) => {
    setSelectedService(service);
    setView('schedule-editor');
    
    try {
      const response = await triggerGetSchedule(service.id).unwrap();
      
      let incomingDays: any[] = [];
      if (Array.isArray(response)) {
        incomingDays = response;
      } else if (response && Array.isArray((response as any)?.value)) {
        incomingDays = (response as any).value;
      } else if (response && Array.isArray((response as any)?.data)) {
        incomingDays = (response as any).data;
      } else if (response && Array.isArray((response as any)?.workingDays)) {
        incomingDays = (response as any).workingDays;
      }

      // Match each day index with the API response (checking both number or string name like "Sunday")
      const mergedSchedule: IWorkingDay[] = ORDERED_DAYS_ARRAY.map(dayIndex => {
        const dayName = DAYS_OF_WEEK_NAMES[dayIndex];
        
        const existingDay = incomingDays.find(d => 
          (typeof d.dayOfWeek === 'string' && d.dayOfWeek.trim().toLowerCase() === dayName.toLowerCase()) ||
          d.dayOfWeek === dayIndex
        );

        if (existingDay) {
          return {
            id: existingDay.id,
            dayOfWeek: dayIndex,
            isEnabled: typeof existingDay.isEnabled === 'string' ? existingDay.isEnabled === 'true' : Boolean(existingDay.isEnabled),
            startTime: (existingDay.startTime || '09:00:00').slice(0, 5),
            endTime: (existingDay.endTime || '18:00:00').slice(0, 5)
          };
        }

        return {
          dayOfWeek: dayIndex,
          isEnabled: false,
          startTime: '09:00',
          endTime: '18:00'
        };
      });

      setWorkingDays(mergedSchedule);
    } catch (err) {
    
      setWorkingDays(
        ORDERED_DAYS_ARRAY.map(dayIndex => ({
          dayOfWeek: dayIndex,
          isEnabled: false,
          startTime: '09:00',
          endTime: '18:00'
        }))
      );
    }
  };

  const handleToggleDay = (dayIndex: number) => {
    setWorkingDays(prev => prev.map(day => 
      day.dayOfWeek === dayIndex ? { ...day, isEnabled: !day.isEnabled } : day
    ));
  };

  const handleTimeChange = (dayIndex: number, field: 'startTime' | 'endTime', value: string) => {
    setWorkingDays(prev => prev.map(day => 
      day.dayOfWeek === dayIndex ? { ...day, [field]: value } : day
    ));
  };

  const handleSaveSchedule = async () => {
    if (!selectedService) return;
    
    // Format full time string for API update payload
    const formattedWorkingDays = workingDays.map(day => ({
      ...day,
      startTime: day.startTime.length === 5 ? `${day.startTime}:00` : day.startTime,
      endTime: day.endTime.length === 5 ? `${day.endTime}:00` : day.endTime
    }));

    const payload: IServiceWorkingDaysRequest = {
      serviceId: selectedService.id,
      workingDays: formattedWorkingDays
    };

    try {
      await saveWorkingDay(payload).unwrap();
      alert("Working schedule saved successfully!");
    } catch (err) {
      console.error("API execution fault saving working schedule:", err);
      alert("Failed to save schedule.");
    }
  };

  const sortedServices = [...services].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="min-h-screen px-4 py-6">
      
      {/* VIEW 1: SELECT SERVICE */}
      {view === 'services-select' && (
        <div className="max-w-[1400px] mx-auto">
          <div className="border-b border-gray-300/40 pb-6 mb-8 md:mb-12">
            <h1 className="text-2xl md:text-3xl font-bold text-logoHeading">Opening Times Setup</h1>
            <p className="text-gray-400 mt-1 text-sm font-medium">
              Select an option below to define weekly availability and business operating hours.
            </p>
          </div>

          {servicesLoading ? (
            <div className="flex flex-col items-center justify-center h-[250px]">
              <Loader2 className="w-8 h-8 md:w-10 md:h-10 animate-spin text-mainBrandColor" />
              <p className="mt-3 text-xs md:text-sm font-semibold text-gray-400 uppercase tracking-wider">Loading Services...</p>
            </div>
          ) : servicesError ? (
            <div className="bg-white rounded-2xl p-6 md:p-10 text-center border border-mediumgray max-w-md mx-auto shadow-sm">
              <AlertCircle className="w-10 h-10 text-red-500 mx-auto" />
              <h2 className="mt-4 text-base md:text-lg font-bold text-gray-800">Connection Error</h2>
              <button 
                onClick={() => triggerGetAllServices()} 
                className="mt-5 bg-logoHeading text-white px-5 py-2.5 rounded-xl text-xs md:text-sm font-semibold hover:opacity-90 transition cursor-pointer"
              >
                <RefreshCw className="w-4 h-4 inline mr-1" /> Retry
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-x-8 md:gap-y-12 justify-center">
              {sortedServices.map((service) => (
                <div
                  key={service.id}
                  onClick={() => handleSelectService(service)}
                  className="group flex flex-col items-center cursor-pointer transition-all duration-300 relative bg-white p-3 md:p-0 rounded-2xl md:bg-transparent border border-gray-100 md:border-0 shadow-xs md:shadow-none"
                >
                  <div className="relative size-[180px] mx-auto">
                    <div className={`w-full h-full overflow-hidden border-[2px] md:border-[3px] transition-all duration-500 bg-gray-50 
                      ${service.isActive 
                        ? 'border-mainBrandColor rounded-[48%_52%_68%_32%_/_42%_48%_52%_58%] group-hover:rounded-[52%_48%_35%_65%_/_48%_55%_45%_52%] group-hover:shadow-md' 
                        : 'border-gray-200 rounded-[50%] opacity-60'
                      }`}
                    >
                      <img
                        src={service.logoUrl || 'https://images.unsplash.com/photo-1517849845537-4d257902454a?w=400'}
                        alt={service.name}
                        className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-105 ${!service.isActive ? 'grayscale' : ''}`}
                      />
                    </div>
                  </div>
                  <div className="mt-3 md:mt-4 text-center space-y-0.5 md:space-y-1 w-full px-1">
                    <h3 className="text-sm md:text-lg font-bold text-logoHeading group-hover:text-mainBrandColor transition-colors duration-200 line-clamp-1">
                      {service.name}
                    </h3>
                    <span className="text-[9px] md:text-[10px] font-extrabold uppercase tracking-widest text-gray-400 group-hover:text-logoHeading transition-colors block">
                      Set Schedule &rarr;
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* VIEW 2: OPENING TIMES EDITOR */}
      {view === 'schedule-editor' && selectedService && (
        <div className="max-w-4xl mx-auto space-y-4 md:space-y-6">
          
          <button
            type="button"
            onClick={() => { setView('services-select'); setSelectedService(null); }}
            disabled={isSaving}
            className="inline-flex items-center gap-2 text-xs md:text-sm font-bold text-gray-400 hover:text-logoHeading transition-colors cursor-pointer disabled:opacity-50 group"
          >
            <ArrowLeft className="w-3.5 h-3.5 md:w-4 md:h-4 group-hover:-translate-x-0.5 transition-transform stroke-[2.5]" />
            Back to Services List
          </button>

          <div className="bg-white rounded-2xl md:rounded-3xl p-4 md:p-8 shadow-sm border border-mediumgray space-y-4 md:space-y-6">
            
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-gray-100 pb-4 md:pb-5">
              <div className="space-y-0.5 md:space-y-1">
                <h1 className="text-2xl md:text-4xl font-extrabold text-logoHeading tracking-tight">Opening Times</h1>
                <p className="text-xs md:text-sm text-headerButtonColor font-bold tracking-wide">
                  {selectedService.name.toUpperCase()} / Regular schedule
                </p>
              </div>
            </div>

            {scheduleLoading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Loader2 className="w-7 h-7 text-mainBrandColor animate-spin" />
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mt-2">Loading Schedule...</p>
              </div>
            ) : scheduleError ? (
              <div className="p-4 rounded-xl bg-red-50 text-red-700 flex items-center gap-3 text-xs md:text-sm font-semibold">
                <ShieldAlert className="w-5 h-5 shrink-0" />
                <span>Error syncing operational profiles. You can set new times and save.</span>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {ORDERED_DAYS_ARRAY.map((dayIndex) => {
                  const dayConfig = workingDays.find(d => d.dayOfWeek === dayIndex) || {
                    dayOfWeek: dayIndex,
                    isEnabled: false,
                    startTime: '09:00',
                    endTime: '18:00'
                  };

                  return (
                    <div 
                      key={dayIndex}
                      className={`py-4 flex flex-col justify-center lg:flex-row lg:items-center lg:justify-between gap-3 lg:gap-4 transition-all ${
                        !dayConfig.isEnabled ? 'bg-lightgray/40 opacity-60 rounded-xl px-2 sm:px-0' : 'bg-white'
                      }`}
                    >
                      
                      {/* Left: Switch & Day Label */}
                      <div className="flex items-center gap-4 min-w-[160px] md:min-w-[180px]">
                        <button
                          type="button"
                          role="switch"
                          aria-checked={dayConfig.isEnabled}
                          onClick={() => handleToggleDay(dayIndex)}
                          disabled={isSaving}
                          className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                            dayConfig.isEnabled ? 'bg-mainBrandColor' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition duration-200 ease-in-out ${
                              dayConfig.isEnabled ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                        
                        <span className={`text-sm md:text-base font-bold ${dayConfig.isEnabled ? 'text-slate-800' : 'text-gray-400 font-medium'}`}>
                          {DAYS_OF_WEEK_NAMES[dayIndex]}
                        </span>
                      </div>

                      {/* Right: Time Fields */}
                      <div className="flex items-center justify-center lg:justify-end flex-1 w-full lg:max-w-md">
                        <div className={`flex items-center gap-2 w-full ${!dayConfig.isEnabled ? 'pointer-events-none opacity-40' : ''}`}>
                          
                          {/* Start Time */}
                          <div className="relative flex-1">
                            <Clock className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5 pointer-events-none" />
                            <input 
                              type="time"
                              required
                              disabled={isSaving || !dayConfig.isEnabled}
                              value={dayConfig.startTime}
                              onChange={(e) => handleTimeChange(dayIndex, 'startTime', e.target.value)}
                              className="w-full text-center pl-7 pr-2 py-2 border border-mediumgray bg-lightgray rounded-xl text-xs font-bold text-slate-800 focus:border-mainBrandColor focus:bg-white outline-none transition"
                            />
                          </div>
                          
                          <span className="text-gray-400 font-extrabold text-xs px-0.5 shrink-0">to</span>

                          {/* End Time */}
                          <div className="relative flex-1">
                            <Clock className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5 pointer-events-none" />
                            <input 
                              type="time"
                              required
                              disabled={isSaving || !dayConfig.isEnabled}
                              value={dayConfig.endTime}
                              onChange={(e) => handleTimeChange(dayIndex, 'endTime', e.target.value)}
                              className="w-full text-center pl-7 pr-2 py-2 border border-mediumgray bg-lightgray rounded-xl text-xs font-bold text-slate-800 focus:border-mainBrandColor focus:bg-white outline-none transition"
                            />
                          </div>

                        </div>
                      </div>

                    </div>
                  );
                })}
              </div>
            )}

            {/* Bottom Actions */}
            <div className="flex flex-col sm:flex-row items-center justify-end gap-2 pt-5 border-t border-gray-100">
              <button
                type="button"
                onClick={() => { setView('services-select'); setSelectedService(null); }}
                disabled={isSaving}
                className="w-full sm:w-auto text-center border border-mediumgray bg-white px-5 py-2.5 rounded-xl text-xs uppercase tracking-wider font-bold text-gray-500 hover:bg-lightgray transition cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
              
              <button
                type="button"
                onClick={handleSaveSchedule}
                disabled={isSaving}
                className="w-full sm:w-auto bg-logoHeading hover:bg-mediumGreen text-white px-6 py-2.5 rounded-xl text-xs uppercase tracking-wider font-bold shadow-sm transition inline-flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
              >
                {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin stroke-[2.5]" /> : <Save className="w-3.5 h-3.5" />}
                Save Changes
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}